import { v4 as uuidv4 } from "uuid";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { known_chart_ids } from "hooks/useChartsStore";
import debounce from 'lodash.debounce';
import LZString from 'lz-string';
import isEqual from "lodash.isequal";
// eslint-disable-next-line
import {getAllowedDatasets, getDistinctAttributeValues, existingComplexFilters, existingSimpleFilters} from 'core/utils/data-helper-functions';
import { dedupe_max_limit } from "../core/utils/constants";
import { usePrevious } from "./usePrevious";

const h3 = require("h3-js");

// map zoom to h3 precision lookup
export const hex_precision_lookup = {
  4: 3,
  5: 5.5,
  6: 7,
  7: 8.5,
  8: 10,
  9: 11.8,
};
export const points_precision_lookup = {
  4: 3,
  5: 3.9,
  6: 5.3,
  7: 6.6,
  8: 8.1,
  9: 9.2,
  10: 10.4,
  11: 11.5,
  12: 12.5,
};

const needs_counts_url = ['explorer', 'order', 'model'];

/**
 * Checks the url to see if a counts api call is necessary 
 */
const needsCounts = () => {
  return needs_counts_url.some((name) => window.location.pathname.includes(name));
}

export default function useCrossfilterStore(
  appState,
  chartsStore,
  command,
  query
) {

  const access = appState.userCan;

  const [loaded, setLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [audienceLoaded, setAudienceLoaded] = useState(false);

  const [filters, setFilters] = useState(
    JSON.parse(localStorage.getItem("am:audience:filters") || "{}")
  );

  const [widgets, setWidgets] = useState(
    JSON.parse(localStorage.getItem("am:audience:widgets") || "[]")
  );
  const [adhoc_points, setAdhocPoints] = useState(
    JSON.parse(localStorage.getItem("am:audience:adhoc_points") || "[]")
  );
  const [location_type_points, setLocationTypePoints] = useState([]);

  const [location_type_ids, setLocationTypeIds] = useState(
    JSON.parse(localStorage.getItem("am:audience:location_type_ids") || "[]")
  );

  const [dedupe, setDedupe] = useState(
    localStorage.getItem("am:audience:dedupe_level") || "Individual"
  );

  const [audience_uuid] = useState(
    localStorage.getItem("am:audience:uuid") || uuidv4()
  );

  const [active_charts, setActiveCharts] = useState({});
  const prevActiveCharts = useRef(active_charts);
  // Refs for tracking previous values to avoid unnecessary refresh_counts calls
  const prevMapBounds = useRef(undefined);
  const prevMapZoom = useRef(null);
  const prevPointsLayers = useRef([]);
  const prevHexLayers = useRef([]);
  const prevDedupe = useRef(null);
  const prevComplexFilters = useRef({});
  const prevAdhocPoints = useRef([]);
  const prevLocationsFeatureBuilt = useRef(null);
  // Track first refresh time to ignore map/layer settling after initial load
  const firstRefreshTime = useRef(null);
  // Track if layers have been initialized (needed before first refresh)
  const layersInitialized = useRef(false);
  const isFirstModuleRender = useRef(true);
  const chartsStoreRef = useRef(chartsStore);
  chartsStoreRef.current = chartsStore;
  const [count, setCount] = useState();
  const [counts_filtered, setCountsFiltered] = useState({});
  const [counts_crossfiltered, setCountsCrossfiltered] = useState({});

  const [map, setMap] = useState(null);
  const prevMap = usePrevious({map});
  const [map_visible, setMapVisible] = useState(false);
  const [map_covered, setMapCovered] = useState(false);
  const [map_bounds, setMapBounds] = useState(undefined);

  const [dedupeLevels, setDedupeLevels] = useState([]);

  const [map_center, setMapCenter] = useState(
    JSON.parse(
      localStorage.getItem("am:audience:map:center") ||
      localStorage.getItem("am:audience:map:hq") ||
      // US large
      '{"lat": 41.5686, "lng": -96.2698}'
    )
  );

  const [companyHq, setCompanyHq] = useState(
    JSON.parse(
      localStorage.getItem("am:audience:map:hq") ||
      localStorage.getItem("am:audience:map:center") ||
      // US large
      '{"lat": 41.5686, "lng": -96.2698}'
    )
  );

  const [map_zoom, setMapZoom] = useState(
    localStorage.getItem("am:audience:map:zoom") || 4
  );

  const [points_layers, setPointLayers] = useState([]);
  const [hex_layers, setHexLayers] = useState([]);

  const [loading_chart_data, setLoadingChartData] = useState(true);
  const [loading_map_data, setLoadingMapData] = useState(true);

  const [last_chart_params, setLastChartParams] = useState("");
  const [last_map_params, setLastMapParams] = useState("");
  const [last_location_params, setLastLocationParams] = useState("");

  const [points, setPoints] = useState({});
  const [h3_indexes, setH3Indexes] = useState({});

  const [locationsFeatureBuilt, setLocationsFeatureBuilt] = useState(null);

  const [requestCountRefresh, setRequestCountRefresh] = useState(false);

  // Cached location data - fetched once per company, shared by all components
  const [locationTypesData, setLocationTypesData] = useState(null);
  const [radiusDefaults, setRadiusDefaults] = useState(null);
  const [mapListData, setMapListData] = useState(null);

  const [audience, setAudience] = useState(JSON.parse(LZString.decompress(localStorage.getItem("am:audience:audience"))|| "{}"));

  const [complexFilters, setComplexFilters] = useState(
    JSON.parse(LZString.decompress(localStorage.getItem("am:audience:complexFilters")) || "{}")
  );

  const [updatedComplexFilters, setUpdatedComplexFilters] = useState({});

  const [isComplex, setIsComplex] = useState(
    JSON.parse(localStorage.getItem("am:audience:is_complex") || false)
  );

  const [missingFilters, setMissingFilters] = useState(false);
  const [missingComplexFilters, setMissingComplexFilters] = useState(false);
  const [noFilters, setNoFilters] = useState(false);
  const [allowedDatasets, setAllowedDatasets] = useState([]);
  const [geoPrefix, setGeoPrefix] = useState("");
  
  // eslint-disable-next-line
  const [onMapUpdateHandler, setOnMapUpdateHandler] = useState(() => (x) => {});


  /* explorer modules section */
  const EXPLORER_MODULES = [];
  if(access("explorer_consumers_view")) {
    EXPLORER_MODULES.push("consumers");
  }
  if(access("explorer_movers_view")) {
    EXPLORER_MODULES.push("movers");
  }
  if(access("explorer_lifeevents_view")) {
    EXPLORER_MODULES.push("life_events");
  }

  // Initialize explorerModule from localStorage
  const [explorerModule, setExplorerModuleState] = useState(() => {
    let stored_explorer_module = localStorage.getItem("am:explorer_module");
    if(!stored_explorer_module) {
      localStorage.setItem("am:explorer_module", "consumers");
    }
    return stored_explorer_module || "consumers";
  });

  // Wrapper function that updates both state and localStorage
  const setExplorerModule = (value) => {
    setExplorerModuleState(value);
    localStorage.setItem("am:explorer_module", value);
  };

  useEffect(() => {
    if (isFirstModuleRender.current) {
      isFirstModuleRender.current = false;
      return;
    }
    localStorage.removeItem("explorer:layers:boundaries");
    localStorage.removeItem("am:audience:lastid");
    localStorage.removeItem("am:audience:audience");
    localStorage.removeItem("am:audience:audience_state");
    localStorage.removeItem("am:audience:filters");
    localStorage.removeItem("am:audience:location_type_ids");
    localStorage.removeItem("am:audience:adhoc_points");
    localStorage.removeItem("am:audience:complexFilters");
    localStorage.removeItem("am:audience:is_complex");
    localStorage.removeItem("am:audience:widgets");
    restoreFromAudience("clear_all");
    refresh_charts_counts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explorerModule])

  const orderExpirationTime = window.order_expiration_days;

  // get match levels if present for the gather part of the counts call. 
  const getMatchLevels = (feature) => {
    if (!filters || !Object.keys(filters).length) return [];
    const foundFeature = filters[feature.id] || {};
    let globalMatchLevel = null;
    let filterMatchLevels = [];
    if (foundFeature.length) {
      globalMatchLevel = foundFeature[0]?.all_match_value;
      if (globalMatchLevel === "file") {
        foundFeature.forEach((f) => {
          if (f.match_value && f.match_value !== "file" && f.match_value !== "individual") {
            filterMatchLevels.push([f.value, f.match_value]);
          }
        });
        globalMatchLevel = filterMatchLevels.length ? filterMatchLevels : "file";
      } else {
        foundFeature.forEach((f) => {
          if (f.match_value && f.match_value !== "file" && f.match_value !== "individual") {
            filterMatchLevels.push([f.value, globalMatchLevel]);
          }
        });
        globalMatchLevel = filterMatchLevels.length ? filterMatchLevels : globalMatchLevel;
      }
    }
    return filterMatchLevels.length ? filterMatchLevels : globalMatchLevel;
  }

   const apiSearchEndpoints = {
      _county_fips: "location/get-county-names-map",
      _congress: "location/get-cd-names-map",
      _cbsa: "location/get-cbsa-names-map",
    };


  function chartsGather() {
    if (!chartsStore.loaded) return [];
    return Array.from(
      new Set(
        Object.entries(active_charts)
          .filter((entry) => entry[1] > 0) // chart count > 0
          .map((entry) => {
            const chart_id = entry[0];
            if (chart_id === known_chart_ids.locations) return [];
            const chart = chartsStore.charts[chart_id];
            if (chart) {
              const matchLevels = getMatchLevels(chart);
              if (Array.isArray(matchLevels)) {
                return [
                  {
                    feature: {
                      ds: chart.ds,
                      id: chart.id,
                      value_match_levels: matchLevels,
                    },
                  },
                ];
              } else {
                return [
                  {
                    feature: {
                      ds: chart.ds,
                      id: chart.id,
                      whole_feature_match_level: matchLevels,
                    },
                  },
                ];
              }
            } else {
              return [];
            }
          })
          .reduce((acc, series) => acc.concat(series), [])
      )
    );
  }

  function mapGather() {
    if (!chartsStore.loaded) return [];
    if (!map_visible || map_covered) return [];
    const person_type_chart = chartsStore.charts[known_chart_ids.person_type];

    const gathers = [];
    if (hex_layers.length && person_type_chart) {
      const precision = Object.entries(hex_precision_lookup).reduce(
        (acc, [_precision, cutoff]) => (map_zoom >= cutoff ? _precision : acc),
        1
      );
      const gather = {
        feature: {
          ds: person_type_chart.ds,
          id: person_type_chart.id,
        },
        pivot: chartsStore.h3FeatureMap[`${geoPrefix}H3_${precision}`],
        alias: "hex",
      };
      gathers.push(gather);
    }
    if (points_layers.length && person_type_chart) {
      const precision = Object.entries(points_precision_lookup).reduce(
        (acc, [_precision, cutoff]) => (map_zoom >= cutoff ? _precision : acc),
        1
      );
      const gather = {
        feature: {
          ds: person_type_chart.ds,
          id: person_type_chart.id,
        },
        pivot: chartsStore.h3FeatureMap[`${geoPrefix}H3_${precision}`],
        alias: "points",
      };
      gathers.push(gather);
    }
    return gathers;
  }

  function validate_sftp_directory(company_code, directory) {
    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
    };

    return (
      command(`run/validate_sftp_directory/${company_code}/${directory}`, {}, xhrConfig)
        .then((response) => {
          if (response.hasOwnProperty("valid_directory")) {
            return response.valid_directory;
          } else {
            return false;
          }
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
        })
    );
  }

  function extractFeaturesWithAllValues(json) {
    let featureMap = new Map();

    function traverse(obj) {
        if (Array.isArray(obj)) {
            obj.forEach(traverse);
        } else if (typeof obj === "object" && obj !== null) {
            if (obj.eq && obj.eq.feature && obj.eq.feature.id && obj.eq.value !== undefined) {
                let featureId = obj.eq.feature.id;
                if (!featureMap.has(featureId)) {
                    featureMap.set(featureId, {
                        ...obj.eq.feature,
                        values: new Set()
                    });
                }
                featureMap.get(featureId).values.add(obj.eq.value);
            }
            if (obj.not) {
                traverse(obj.not); // Handle "not" clauses
            }
            Object.values(obj).forEach(traverse);
        }
    }

    traverse(json);

    // Convert sets to arrays for final output
    return Array.from(featureMap.values()).map(entry => ({
        ...entry,
        values: [Array.from(entry.values)]
    }));
  }

  function fillComplexFilterData(cf) {
    const charts = chartsStore.charts;
    cf.forEach(feature => {
      let newValues = [];
      const chartFeature = charts[feature.id];
      feature.values[0].forEach(val => {
        newValues.push({
          label: chartFeature.value_labels[val],
          value: val
        });
      });
      feature.filter_name = chartFeature.label;
      feature.filter_values = newValues;
      feature.ignore_missing = false;
      delete feature.values;
    });
      return cf;
  }

  async function fetchGeoBoundaryNames(ids, type) {
    type = type.replace("_le", "");
    const apiUrl = apiSearchEndpoints[type];
    if (!apiUrl) return null; 
    const res = await command(apiUrl, ids);
    return res || null;
  }

  function build_saved_selection_criteria() {
    let selection_criteria_to_store = [];
    if (isComplex) {
      const complexJason = parseComplexFilters(complexFilters);
      selection_criteria_to_store = fillComplexFilterData(extractFeaturesWithAllValues(complexJason));
    } else {
      let filters_to_store = getFiltersSavedSelectionCriteria();
      let filters_info_values = Object.values(chartsStore.charts);
  
      // remove any filter without values
      filters_to_store && filters_to_store.length && filters_to_store.forEach((item) => {
        if(item && Object.keys(item).length !== 0 && item.hasOwnProperty("filter_values")) {
        // add id and ds values
        let find_by_label = filters_info_values.find(_id => _id.label === item.filter_name);
        selection_criteria_to_store.push({...item, ds: find_by_label.ds, id: find_by_label.id});
        }
      })
    }
    return selection_criteria_to_store;
  }
  
  function computeDatasets(result_filters) {
    let dsAllValue = appState.activeCompany.data_permissions.find(entry => entry.name === "ds_all")?.value;
    if (dsAllValue !== undefined && dsAllValue.length > 0) {
      const dsAll = dsAllValue.split(",");
      let _suppressed = getAllDatasets();
      return getAllowedDatasets(result_filters, dsAll, _suppressed);
    }
    return undefined;
  }

  function fixDedupe(_dedupe, _population) {
    const rawDedupe = (!_dedupe || _dedupe === 'undefined') ? getDedupe() : _dedupe;
    const resolvedDedupe = rawDedupe.replace(/^(LE_|MV_)/, '');
    if(_population === "life_events") {
      return `LE_${resolvedDedupe}`;
    }
    if(_population === "movers") {
      return `MV_${resolvedDedupe}`;
    }
    return resolvedDedupe;
  }

  function save_audience(_audience, persist = true, set_population=null) {
    if (!_audience) return;

    if(persist) {
      setAudience(_audience);
    }

    let filter = {};
    let audience_dedupe = fixDedupe(_audience?.dedupe_level, _audience?.population_domain);

    if(_audience.is_complex) {
      // if audience has complex filters
      filter = parseComplexFilters(_audience.complex_filters?.logic);
    } else {
      filter = _audience.filters ? filters2json(false, _audience.filters) : getFilterJson();
    }

    const _data_sets = computeDatasets(filter.and);
    const params = {
      filter,
      allowed_datasets: _data_sets !== undefined ? _data_sets : allowedDatasets,
      options: [
        {
          save: {
            id: `${_audience.id || audience_uuid}`,
            description: _audience.name || "test description",
            population_domain: _audience?.population_domain || explorerModule,
          },
        },
      ],
      dedupe: audience_dedupe,
      population_domain: _audience?.population_domain || explorerModule,
    };

    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
    };

    command(`run/counts/${appState.activeCompany.code}`, params, xhrConfig)
      .then(() => {
        if(set_population) {
          // after copying an audience switch back to the current population
          const reload_audience = localStorage.getItem("am:audience:reload");
          setExplorerModule(set_population);
          setTimeout(() => {
            reload_audience?.length && localStorage.setItem("am:audience:lastid", reload_audience);
            localStorage.removeItem("am:audience:reload");
          }, 1000)
        }
        
        // Saving removes old location fields in crossfilter, so add back after delay
        const chart_id = known_chart_ids.locations;
        // Delay - anything lower than 5 sec may not work.
        setTimeout(
          () => build_location_selects(filters[chart_id] || {}, true),
          7000
        );
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  function delete_audience(_audience) {
    setAudience(_audience);
    const params = {
      filter: filters2json(),
      options: [
        {
          delete: {
            id: `${_audience.id || audience_uuid}`,
          },
        },
      ],
      dedupe: getDedupe(),
      population_domain: explorerModule
    };

    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
    };

    command(`run/counts/${appState.activeCompany.code}`, params, xhrConfig)
      .then(() => {
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  function quote_order(order, onComplete, onError) {
    const xhrConfig = {};

    const params = Object.assign(order, {
      filter: getFilterJson(),
      allowed_datasets: allowedDatasets,
      filters: filters,
      dedupe_level: getDedupe(),
      is_complex: isComplex,
      population_domain: explorerModule
    });

    command("order/quote", params, xhrConfig)
      .then((response) => {
        onComplete(response);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
        onError(error);
      });
  }

  function save_order(order, onComplete, onError) {
    const xhrConfig = {};

    const params = Object.assign(order, {
      filter: getFilterJson(),
      allowed_datasets: allowedDatasets,
      filters: filters,
      complex_filters: complexFilters,
      insights: widgets,
      dedupe_level: getDedupe(),
      adhoc_points: adhoc_points,
      is_complex: isComplex,
      population_domain: explorerModule,
      saved_selection_criteria: build_saved_selection_criteria(),
      map_state: {
        map_center: map_center,
        map_zoom: map_zoom,
      },
    });

    if (!!order.id) {
      command("order/update", params, xhrConfig, {
        defaultErrorHandling: true,
      })
        .then((response) => {
          onComplete(response);
        })
        .catch((error) => {
          console.log("Order Update Failed", JSON.stringify(error));
        });
    } else {
      command("order/create", params, xhrConfig, {
        defaultErrorHandling: false,
      })
        .then((response) => {
          onComplete(response);
        })
        .catch((error) => {
          console.log("Order Create Failed", JSON.stringify(error));
        });
    }
  }

  function submit_order(order, onComplete, onError, orderReview={}) {
    const xhrConfig = {};
    // create YYYYMMDD format order submission date and order expiration dates
    // for inclusion in the output order
    let curr_date = new Date();
    let submit_date = curr_date.getFullYear().toString() +
      (curr_date.getMonth()+1).toString().padStart(2,'0') +
      curr_date.getDate().toString().padStart(2,'0');
    let expiration_date = curr_date;
    expiration_date.setDate(expiration_date.getDate() + orderExpirationTime);
    let expiration_date_str = expiration_date.getFullYear().toString() +
      (expiration_date.getMonth()+1).toString().padStart(2,'0') +
      expiration_date.getDate().toString().padStart(2,'0');

    const params = Object.assign(order, {
      filter: getFilterJson(),
      allowed_datasets: allowedDatasets,
      filters: filters,
      complex_filters: complexFilters,
      is_complex: isComplex,
      submit_date_value: submit_date,
      dedupe_level: getDedupe(),
      expiration_date_value: expiration_date_str,
      population_domain: explorerModule,
    });

    if(orderReview && Object.keys(orderReview).length > 0) {
      if(orderReview.approved) {
        params.cost_limit_excess_approved = true;
      } else if(orderReview.denied) {
        command(`order/deny?denialReason=${orderReview.reason}`, params, xhrConfig)
          .then(() => {})
          .catch((error) => {
            console.log("Order Deny Failed", JSON.stringify(error));
          });
        return;
      }
    }

    command("order/submit", params, xhrConfig)
      .then((response) => {
        onComplete(response);
      })
      .catch((error) => {
        if(error && Object.keys(error).length > 0) {
          console.log("Order Submit Failed", JSON.stringify(error));
          onError(error);
        }
      });
  }

  function build_model(model, onComplete, onError) {
    const xhrConfig = {};

    const params = Object.assign(model, {
      filter: getFilterJson(true),
      allowed_datasets: allowedDatasets,
      filters: filters,
      dedupe_level: getDedupe(),
      saved_selection_criteria: getFiltersReportData(),
      population_domain: explorerModule
    });

    command("model/build", params, xhrConfig)
      .then((response) => {
        onComplete(response);
      })
      .catch((error) => {
        console.log("Build Model Failed", JSON.stringify(error));
        onError(error);
      });
  }

  function score_model(model, onComplete, onError) {
    const xhrConfig = {};

    const params = Object.assign(model);

    command("model/score", params, xhrConfig)
      .then((response) => {
        onComplete(response);
      })
      .catch((error) => {
        console.log("Score Model Failed", JSON.stringify(error));
        onError(error);
      });
  }

  function get_dedupe_levels() {
    const params = {
      population_domain: explorerModule
    };

    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
    };

    query(`fetch/dedupes/${appState.activeCompany.code}`, params, xhrConfig)
      .then((response) => {
        let rawLevels = { 'life_event': [], 'consumers': [], 'movers': [] };
        let baseLevels = [];
        response.forEach((level) => {
          if (level.startsWith('LE_')) {
            rawLevels['life_event'].push(level);
          } else if (level.startsWith('MV_')) {
            rawLevels['movers'].push(level);
          } else {
            rawLevels['consumers'].push(level);
            baseLevels.push(level);
          }
        });
        setDedupeLevels(baseLevels.reverse());
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  async function refresh_charts_counts() {
    // only get counts if on a page that needs them
    if (!needsCounts()) {
      return;
    }

    const gathers = chartsGather();

    // we need the datasets to sent to the api
    if(!allowedDatasets || !allowedDatasets.length) {
      return;
    }

    const params = {
      filter: getFilterJson(),
      allowed_datasets: allowedDatasets,
      gather: gathers,
      dedupe: getDedupe(),
      locationsFeatureBuilt,
      population_domain: explorerModule,
    };

    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
    };

    const params_changed = JSON.stringify(params) !== last_chart_params;

    if (params_changed) {
      setLastChartParams(JSON.stringify(params));
      delete params["locationsFeatureBuilt"];

      setLoadingChartData(true);
      command(`run/counts/${appState.activeCompany.code}`, params, xhrConfig)
        .then((response) => {
          setCount(response.counts?.__total__.all_filters || 0);
          const tmp_counts = {};
          Object.entries(response.counts).forEach(([key, val]) => {
            let [ds, feature] = key.split("|");
            tmp_counts[ds + "." + feature] = val;
          });

          const crosscounts = Object.assign({}, tmp_counts);
          const crosscount_entries = Object.keys(response.counts).filter(
            (key) => key.indexOf("xcounts__") === 0
          );
          for (let crosscount_key of crosscount_entries) {
            let [ds, feature] = crosscount_key.slice(9).split("|");
            crosscounts[ds + "." + feature] = response.counts[crosscount_key];
          }

          setCountsFiltered(tmp_counts);
          setCountsCrossfiltered(crosscounts);
          setLoadingChartData(false);
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
          setLoadingChartData(false);
        });
    }
  }

  function refresh_map_counts() {
    // only get counts if on a page that needs them
    if (!needsCounts()) {
      return;
    }

    const gathers = mapGather();

    if (!map || !map_bounds) return;
    if(!allowedDatasets || !allowedDatasets.length) {
      return;
    }

    const params = {
      filter: getFilterJson(),
      allowed_datasets: allowedDatasets,
      gather: gathers,
      bounds: map_bounds,
      dedupe: getDedupe(),
      locationsFeatureBuilt,
      population_domain: explorerModule,
    };

    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
    };

    const params_changed = JSON.stringify(params) !== last_map_params;
    if (params_changed) {
      setLastMapParams(JSON.stringify(params));
      delete params["locationsFeatureBuilt"];

      setLoadingMapData(true);
      command(`run/counts/${appState.activeCompany.code}`, params, xhrConfig)
        .then((response) => {
          const totalCount = response.counts?.__total__.all_filters || 0; 
          setCount(totalCount);
          update_points(response);
          update_hexes(response);
          setLoadingMapData(false);
        })
        .catch((error) => {
          setPoints({});
          setH3Indexes({});
          setLoadingMapData(false);
          console.log("Counts Update Failed", JSON.stringify(error));
        });
    }
  }

  function update_points(response) {
    if (response.counts.pivot__points) {
      const tmp_points = {};
      const points_counts = Object.entries(response.counts.pivot__points);
      for (const [fact, counts] of points_counts) {
        const fact_points_counts = Object.entries(counts);
        if (fact_points_counts.length > 0) {
          tmp_points[fact] = [];
          for (const [h3_index, count] of fact_points_counts) {
            tmp_points[fact].push({
              point: h3.h3ToGeo(h3_index).reverse(),
              count,
            });
          }
        }
      }
      setPoints(tmp_points);
    } else {
      setPoints({});
    }
  }

  function update_hexes(response) {
    if (response.counts.pivot__hex) {
      const hexes = {};
      const hex_counts = Object.entries(response.counts.pivot__hex);
      for (const [fact, counts] of hex_counts) {
        const fact_hex_counts = Object.entries(counts);
        if (fact_hex_counts.length > 0) {
          hexes[fact] = [];
          for (const [h3_index, count] of fact_hex_counts) {
            hexes[fact].push({
              boundary: [h3.h3ToGeoBoundary(h3_index, true)],
              point: h3.h3ToGeo(h3_index).reverse(),
              count,
            });
          }
        }
      }
      setH3Indexes(hexes);
    } else {
      setH3Indexes({});
    }
  }

  function getDefaultMapZoom() {
    const hqCoords = companyHq;
    const usCoords = { "lat": 41.5686, "lng": -96.2698 };
    let zoomLevel = 4;
    zoomLevel = (isEqual(hqCoords, usCoords)) ? 4 : 9;
    return zoomLevel;
  }

  function getFilterJson(is_model=false) {
    if (
      Object.keys(complexFilters).length === 0 &&
      complexFilters.constructor === Object
    ) {
      setIsComplex(false);
      return filters2json(is_model);
    } else {
      setIsComplex(true);
      return complexFilters2json();
    }
  }

  /* helper function to get all current filter except adhoc locations */
  function removeAdhocFilters(obj) {
    if (Array.isArray(obj)) {
      // Filter out any items that should be removed, then recursively process remaining items
      return obj
        .filter(item => !shouldRemove(item))
        .map(item => removeAdhocFilters(item))
        .filter(item => {
          // Remove empty arrays or objects after processing
          if (Array.isArray(item)) return item.length > 0;
          if (typeof item === 'object' && item !== null) {
            return Object.keys(item).length > 0;
          }
          return true;
        });
    }
    
    if (typeof obj === 'object' && obj !== null) {
      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        const processed = removeAdhocFilters(value);
        
        // Only add the key if the processed value is not empty
        if (Array.isArray(processed) && processed.length === 0) continue;
        if (typeof processed === 'object' && processed !== null && Object.keys(processed).length === 0) continue;
        
        result[key] = processed;
      }
      return result;
    }
    
    return obj;
  }

  function shouldRemove(item) {
    // Check if this is an "eq" filter with a value containing "adhoc_"
    if (item && typeof item === 'object' && item.eq && item.eq.value) {
      return item.eq.value.includes('adhoc_');
    }
    return false;
  }

  function mergeFilterWithCleaned(cleanedFilters, newFilter) {
    // Deep clone to avoid mutating original
    const updated_filters = JSON.parse(JSON.stringify(cleanedFilters));
    
    // If the cleaned filters is empty or has no "and" array, create structure
    if (!updated_filters.and || !Array.isArray(updated_filters.and)) {
      return {
        "and": [newFilter]
      };
    }
    
    // Add the new filter to the top-level "and" array
    updated_filters.and.push({
      "and": [
        {
          "or": [newFilter]
        }
      ]
    });
    
    return updated_filters;
  }

  // get counts on individual ad-hoc locations
  function locationsCount(loc_filter, key) {
    // get all filters
    let locFilters = getFilterJson();

    // set to the actual ad-hoc locations
    locFilters = locFilters.and[0].and[0].or[0];
    
    let locFilter = null;
    
 // find the individual location filters from all filters
    locFilters.or.forEach((a, i) => { 
      if (a.eq.feature.id.includes('adhoc')) {
        locFilter = locFilters.or.find((x) => {
          const id = x.eq.value.split("_")[1];
          return loc_filter.location.id.toString() === id;
        });
      }
    });

    /* get all filters NOT adhoc from the current filters */
    let all_filters = removeAdhocFilters(getFilterJson());

    /* add other filters to adhoc to make the api call for counts */
    let merged_filters = mergeFilterWithCleaned(all_filters, locFilter)

    let params = {
      filter: merged_filters,
      allowed_datasets: allowedDatasets,
      gather: mapGather(),
      bounds: map_bounds,
      dedupe: getDedupe(),
      locationsFeatureBuilt,
      population_domain: explorerModule,
    }

    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
    };

    // request count for individual filter
    return command(
      `run/counts/${appState.activeCompany.code}`,
      params,
      xhrConfig
    );
  }

  function quickCount() {
    if (!needsCounts()) {
      return;
    }
    const params = {
      filter: getFilterJson(),
      allowed_datasets: allowedDatasets,
      population_domain: explorerModule,
    };

    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
    };

    return command(
      `run/counts/${appState.activeCompany.code}`,
      params,
      xhrConfig
    );
  }

  const refresh_counts = useCallback(() => {
    if (!needsCounts()) {
      return;
    }
    if (!appState.loaded || !(appState.identity && appState.identity.logged_in)) {
      return;
    }
      
    // Validate
    quickCount().then((response) => {
      if (
        response.counts?.__total__.all_filters > dedupe_max_limit &&
        "string" === typeof dedupe &&
        "string" === typeof dedupeLevels[0] &&
        dedupe !== dedupeLevels[0]
      ) {
        setDedupe(dedupeLevels[0]);
      } else {
        refresh_charts_counts();
        refresh_map_counts();
      }
    });
    // eslint-disable-next-line
  }, [
    loaded,
    mapReady,
    map_bounds,
    map_zoom,
    points_layers,
    hex_layers,
    locationsFeatureBuilt,
    isComplex,
    filters,
    dedupe,
    active_charts,
    adhoc_points,
    complexFilters,
  ]);

  function chart_enter(chart_id) {
    setActiveCharts((prevValue) => {
      prevValue[chart_id] = prevValue[chart_id] ? prevValue[chart_id] + 1 : 1;
      return { ...prevValue };
    });
  }

  function chart_leave(chart_id) {
    if(!Array.isArray(widgets)) { return; }
    setActiveCharts((prevValue) => {
      prevValue[chart_id] = prevValue[chart_id] ? prevValue[chart_id] - 1 : 0;
      if (
        !widgets.includes(chart_id) &&
        !filters[chart_id] &&
        prevValue[chart_id] === 0
      ) {
        delete prevValue[chart_id];
      }
      return { ...prevValue };
    });
  }

  function map_move(ev) {
    const _map = ev.target;
    const _bounds = _map.getBounds();
    // Round zoom to 2 decimal places to avoid floating-point noise triggering refreshes
    setMapZoom(Math.round(_map.getZoom() * 100) / 100);
    setMapCenter(_map.getCenter());
    setMapBounds({ sw: _bounds.getSouthWest(), ne: _bounds.getNorthEast() });
  }

  function map_enter(_map) {
    if (prevMap.map && isEqual(prevMap.map, _map)) return;
    const _bounds = _map.getBounds();
    setMapBounds({ sw: _bounds.getSouthWest(), ne: _bounds.getNorthEast() });
    setMapCenter(_map.getCenter());
    // Round zoom to 2 decimal places to avoid floating-point noise triggering refreshes
    setMapZoom(Math.round(_map.getZoom() * 100) / 100);
    setMapVisible(true);
    setMap(_map);

    _map.on("moveend", map_move);
  }

  function map_leave() {
    setMapVisible(false);
    setMapBounds(undefined);
    setMap(undefined);
  }

  function add_widget(chart_id) {
    if(!Array.isArray(widgets)) { return; }
    if(widgets.includes(chart_id)) {
      return;
    }
    setWidgets((prevValue) => {
      return [...prevValue, chart_id];
    });
  }

  function remove_widget(chart_id) {
    setWidgets((prevValue) => {
      return prevValue.filter((id) => id !== chart_id);
    });
  }

  function add_empty_filter(chart_id) {
    setFilters((prevValue) => {
      if (prevValue[chart_id]) return prevValue;
      return { ...prevValue, [chart_id]: [] };
    });
  }

  function add_location_type_filter(chart_name) {
    const chartNameBase = chart_name.split('_').slice(0, -1).join('_');
    setFilters((prevValue) => {
      for (const [key, val] of Object.entries(prevValue)) {
        if (key.split('_').slice(0, -1).join('_') === chartNameBase) {
          // rename matching key without mutating prevValue
          const next = { ...prevValue };
          delete next[key];
          next[chart_name] = val || [];
          return next;
        }
      }
      // no matching key found, add new one
      if (prevValue[chart_name]) return prevValue;
      return { ...prevValue, [chart_name]: [] };
    });
  }

  function add_filter(chart_id, series, value, is_negated, extra = {}) {

      const hasMultiple = Array.isArray(value);
      let newChartFilters = [];
      const new_filter_entry = Object.assign({}, extra, {
        series: series,
        value: value,
        is_negated: is_negated,
      });

      if(hasMultiple) {
        value.forEach((zip, key, value) => {
          let cleanZip = zip.trim();
          let new_entry = { ...new_filter_entry, value: cleanZip }
          newChartFilters.push(new_entry);

          if (Object.is(value.length - 1, key)) {
            // execute logic on last item
            updateFilters();
          }
        });
      } else {
        newChartFilters.push(new_filter_entry);
        updateFilters();
      }

      function updateFilters() {
        let newValue = filters[chart_id] ? filters[chart_id].concat(newChartFilters) : newChartFilters;
        let uniqueValues = removeDuplicateCodes(newValue);
        storeValues(uniqueValues);
      }

      function removeDuplicateCodes(arr) {
        const key = 'value';
        return [...new Map(arr.map(item => [item[key], item])).values()];
      }

      function storeValues(val) {
        setFilters((prevValue) => {
          return { ...prevValue, [chart_id]: val };
        });
      }
  }

  function getDedupe() {
    const rawDedupe = (!dedupe || dedupe === 'undefined') ? 'Individual' : dedupe;
    const resolvedDedupe = rawDedupe.replace(/^(LE_|MV_)/, '');
    if(explorerModule === "life_events") {
      return `LE_${resolvedDedupe}`;
    }
    if(explorerModule === "movers") {
      return `MV_${resolvedDedupe}`;
    }
    return resolvedDedupe;
  }

  function remove_filter(chart_id, series, value) {
    const newChartFilters = (filters[chart_id] || []).filter(
      (f) => f.series !== series || f.value !== value
    );

    setFilters((prevValue) => {
      return { ...prevValue, [chart_id]: newChartFilters };
    });
  }

  function remove_all_filters(chart_id) {
    if(!chart_id || chart_id === "undefined") { return }
      if(!adhoc_points.length || (chart_id === "locations")) {
        setTimeout(() => {
          setFilters((prevValue) => {
            const next = { ...prevValue };
            delete next[chart_id];
            return next;
          });
      }, 1000); // wait for 1 second before updating the state to deal with multiple updates
      return;
    }
    setFilters((prevValue) => {
      const next = { ...prevValue };
      delete next[chart_id];
      return next;
    });
  }

  function remove_location_type_filter(chart_id) {
    // check if the location exists regardless of radius
    let values = [];
    Object.keys(this.filters).forEach((el) => {
      values.push(el);
    });
    let name = chart_id.split('_')
    name.pop()
    name = name.join('_');

    values.forEach((value) => {
      if(value.includes(name)) {
        setFilters((prevValue) => {
          const next = { ...prevValue };
          delete next[value];
          return next;
        });
      }
    })
  }

  function update_location_type_radius(old_chart_id, new_chart_id) {
    setFilters((prevValue) => {
      const next = { ...prevValue };
      
      // Find and capture the old filter values before removing
      const oldFilters = next[old_chart_id] || [];
      
      // Remove all keys that match the old chart base name
      let baseName = old_chart_id.split('_');
      baseName.pop();
      baseName = baseName.join('_');
      
      Object.keys(next).forEach((key) => {
        if (key.includes(baseName)) {
            delete next[key];
        }
      });
      
      // Add the new key with the preserved filter values in one atomic update
      next[new_chart_id] = oldFilters;
      
      return next;
    });
  }

  function set_filters(chart_id, chartFilters) {
    setFilters((prevValue) => {
      // Check if the filters actually changed
      if (isEqual(prevValue[chart_id], chartFilters)) {
        return prevValue; // Return same reference if no change
      }
      
      return { 
        ...prevValue,
        [chart_id]: chartFilters 
      };
    });
  }

  function set_complex_filters(json_output) {
    setComplexFilters(json_output);
  }

  function remove_complex_filters() {
    setComplexFilters({});
    localStorage.setItem("explorer:complex", false);
    localStorage.setItem("explorer:complexQuery", null);
    localStorage.setItem("am:audience:is_complex", false);
    localStorage.setItem("am:audience:complexFilters", null);
  }

  function update_location_filter(location, filter) {
    const chart_id = known_chart_ids.locations;
    setFilters((prevValue) => {
      const location_filters = prevValue[chart_id] || [];
      const updated = location_filters
        .filter((f) => f.location.id !== location.id)
        .concat({...filter, isNegated: location_filters[0]?.is_negated ? true:false});
      return { ...prevValue, [chart_id]: updated };
    });
  }

  function update_location_filter_multiple(locations, filters) {
    const chart_id = known_chart_ids.locations;
    const location_ids = [];
    locations.forEach((location) => {
      location_ids.push(location.id);
    });
    setFilters((prevValue) => {
      const location_filters = prevValue[chart_id] || [];
      const updated = location_filters
        .filter((f) => !location_ids.includes(f.location.id))
        .concat(filters);
      return { ...prevValue, [chart_id]: updated };
    });
  }

  async function getAudienceDownloadData(charts, type) {
    let data = [];
    if (!charts.length) { return; }
    for (const chart of charts) {
      const vals = await getDownloadValues(chart, type);
      data.push(vals);
    }
    return Promise.all(data);
  }

  // Get Insights data for PDF report
  function getInsightsReportData(_charts) {
    if(!_charts?.length){return;}
    
    let output = [];
    _charts.length && _charts.forEach((chart) => {
      let chart_info = chartsStore.charts[chart];
      let value_series = chart_info.ds + '.' + chart_info.id;
      let dvalues = counts_crossfiltered[value_series];
      let values = [];
      if(typeof dvalues === 'undefined') { 
        return; 
      }
      if (Object.keys(chart_info.value_labels).length !== 0) {
        for (const [key, value] of Object.entries(dvalues)) {
          let label = chart_info.value_labels[key];
          values.push({ label: label, value: value });
        }
      }
      let _out = { 
        "label": chart_info.label,
        "uitype": chart_info.uiType,
        "values": values
      };
      output.push(_out)
    })
    return output;
  }

  // Get Filters data for Audience Saved Selection Criteria
  function getFiltersSavedSelectionCriteria() {
    let _tempFilters = Object.keys(filters);
    let data = [];
    for (let filter of _tempFilters) {
      let _temp = getFilterInfoForSavedSelectionCriteria(filter);
      _temp && data.push(_temp);
    }
    return data;
  }

  function getFilterInfoForSavedSelectionCriteria(chart) {
    if(chart === 'undefined') {  return; }
    let config = chartsStore.charts[chart];
    let value_series = config.ds + '.' + config.id;
    let dfilters = filters[chart] || [];
    let new_values = [];
    let dvalues = config.value_labels || counts_crossfiltered[value_series] || {}; 
    let isExcluded = dfilters.some(obj => obj.is_negated === 1) || false;
    let _out = {
      ignore_missing: false,
      id: config.id,
      ds: config.ds,
      filter_name: config.label,
      is_excluded: isExcluded,
      filter_values: [],
    }

    // check if there are filters but no values, get the values another way
    if(!Object.keys(dvalues).length && dfilters.length) {
      let _temp_values = {};
      dfilters.forEach((item) => {
        _temp_values[item.value] = item.value;
      })
      dvalues = {..._temp_values};
      if( !Object.keys(dvalues).length ) {
        config.value_labels = {..._temp_values};
      }
    }

    if (
      config.uiType === "float-range" ||
      config.uiType === "int-range" 
      ) {
      let int_filters = [];
      dfilters.forEach((filter) => {
        int_filters.push({ label: filter.friendly_name || formatDisplayRange(filter.value), value: filter.value });
      });
      _out = {
        ..._out,
        filter_values: int_filters,
        ignore_missing: true
      }
      return _out;
    }

    if (config.uiType === "locations") {
      let loc_filters = [];
      dfilters.forEach((filter) => {
        loc_filters.push({ label: filter.location.name, value: filter.value });
      });
       _out = {
        ..._out,
         filter_values: loc_filters,
        ignore_missing: true
       }
      return _out;
    }

    if (
      config.uiType === "birthdate-range" ||
      config.uiType === "date-range"
    ) {
      let _date_range = [];
      dfilters.forEach((filter) => {
        _date_range.push({
          label: filter.friendly_name || formatDisplayDateRange(filter.value),
          value: filter.value
        });
      });
      _out = {
        ..._out,
        filter_values: _date_range,
        ignore_missing: true
      }
      return _out;
    }

    if(value_series === `${geoPrefix}county_fips` ||
      value_series === `${geoPrefix}zip` ||
      value_series === `${geoPrefix}congress` ||
      value_series === `${geoPrefix}cbsa`) {
      let _geo_values = [];
      for (const [key] of Object.entries(dvalues)) {
        filters[value_series.split('.')[1]].forEach((item) => {
          if(item.value === key) {
            _geo_values.push({ label: item.friendly_name, value: item.value || item.friendly_name });
          }
        })
      }
      _out = {
        ..._out,
        filter_values: _geo_values
      }
      return _out;
    }

    if(typeof dvalues === 'undefined') { 
      return; 
    }

    if(!Object.keys(dvalues).length) {
      return _out; 
    }

    if (dvalues) {
      const dvalEntries = Object.entries(dvalues);
      for (const [key] of dvalEntries) {
        const label = dvalues[key];
        let filter = {};
          // eslint-disable-next-line
        dfilters.forEach((item) => {
          if (item.value === key) {
            filter.label = label;
            filter.value = item.value;
            filter.match_value = item.match_value;
            new_values.push(filter);
          }
        });
      }
    }

    if(Object.keys(new_values).length) {
      _out = {
        ..._out,
        filter_values: new_values
      }
      return _out;
    }
    return config.uiType;
  }

  // Get Filters data for PDF report
  function getFiltersReportData() {
    let _tempFilters = Object.keys(filters);
    let data = [];
    for (let filter of _tempFilters) {
      let _temp = getFilterInfoForReport(filter);
      _temp && data.push(_temp);
    }
    return data;
  }

  const isModelRegex = /^model_/;

  function getFilterInfoForReport(chart) {
    if(chart === 'undefined') {  return; }
    let config = chartsStore.charts[chart];
    let value_series = config.ds + '.' + config.id;
    let dfilters = filters[chart];
    let new_values = {};
    // dvalues are download values. This set is modified by conditions in this code for each widget type.
    let dvalues = config.value_labels;
    let isExcluded = dfilters.some(obj => obj.is_negated === 1) || false;
    let isModel = isModelRegex.test(config.id);
    let _out = {
      id: config.id,
      ds: config.ds,
      filter_name: config.label,
      is_excluded: isExcluded,
      filter_values: ["No Filters"],
    }

    if (config.uiType === "locations") {
      let adhoc_filters = [];
      dfilters.forEach((filter) => {
        adhoc_filters.push(`Radius: ${filter.value} Location: ${filter.location.name}`);
      });
      _out = {
        ..._out,
        filter_name: "Ad Hoc Location",
        filter_values: adhoc_filters,
      }
      return _out;
    }

    // check if there are filters but no values, get the values another way
    if(!Object.keys(dvalues).length && dfilters.length) {
      let _temp_values = {};
      dfilters.forEach((item) => {
        _temp_values[item.value] = item.value;
      })
      dvalues = {..._temp_values};
      if( !Object.keys(dvalues).length ) {
        config.value_labels = {..._temp_values};
      }
    }

    if(typeof dvalues === 'undefined') { 
      return; 
    }

    if(!Object.keys(dvalues).length && !isModel) {
      return _out; 
    }

    if(isModel) {
      let int_filters = [];
      dfilters.forEach((filter) => {
        int_filters.push(filter.value);
      });
      _out = {
        ..._out,
        filter_values: int_filters
      }
      return _out;
    }

    if (
      config.uiType === "float-range" ||
      config.uiType === "int-range" 
      ) {
      let int_filters = [];
      dfilters.forEach((filter) => {
        int_filters.push(filter.friendly_name || formatDisplayRange(filter.value));
      });
      _out = {
        ..._out,
        filter_values: int_filters
      }
      return _out;
    }

    if (
      config.uiType === "birthdate-range" ||
      config.uiType === "date-range"
    ) {
      let _date_range = [];
      dfilters.forEach((filter) => {
        _date_range.push(filter.friendly_name || formatDisplayDateRange(filter.value));
      });
      _out = {
        ..._out,
        filter_values: _date_range
      }
      return _out;
    }

    if(value_series === `${geoPrefix}county_fips` ||
      value_series === `${geoPrefix}congress` ||
      value_series === `${geoPrefix}cbsa`) {
      let _geo_values = [];
      for (const [key] of Object.entries(dvalues)) {
        filters[value_series.split('.')[1]].forEach((item) => {
          if(item.value === key) {
            _geo_values.push(item.friendly_name);
          }
        })
      }
      _out = {
        ..._out,
        filter_values: _geo_values
      }
      return _out;
    }

    if (dvalues) {
      for (const [key, value] of Object.entries(dvalues)) {
        const label = config.value_labels[key];
          // eslint-disable-next-line
        dfilters.forEach((item) => {
          if (item.value === key) {
            new_values[label || item.value] = {value: value, match_value: item.match_value};
          }
        });
      }
    }

    if(Object.keys(new_values).length) {
      _out = {
        ..._out,
        filter_values: new_values
      }
      return _out;
    }
    return config.uiType;
  }

  // Get CSV data
  async function getDownloadValues(chart, type, units){
    const config = chartsStore.charts[chart];
    const value_labels = config.value_labels;
    const value_series = config.ds + '.' + config.id;
    // dfilters are the actual applied filters.
    let dfilters = filters[chart];
    // dvalues are filtered download count values. This set is modified by conditions in this code for each widget type.
    let dvalues = counts_filtered[value_series];
    let xfvalues = counts_crossfiltered[value_series];

    if (!dvalues && chart !== "locations") return [];

    // Get only filtered values for filter widgets
    let new_values = {};
    if (type === "Filter") {
      if (value_labels && Object.values(value_labels).length !== 0) {
        Object.entries(value_labels).forEach((entry) => { 
          const key = entry[0];
          const label = entry[1];
          const selected = dfilters.find((item) => { return item.value === key });
          const excluded = selected && selected.is_negated;
          if (!excluded && selected) {
            new_values[label] = dvalues[key] || 0;
          } else if (selected) {
            new_values[label] = xfvalues[key] || 0;
          }
        });
        dvalues = new_values;
      } else if (xfvalues && Object.keys(xfvalues).length) {
        Object.entries(xfvalues).forEach((entry) => { 
          const key = entry[0];
          const label = entry[0];
          const selected = dfilters.find((item) => { return item.value === key });
          const excluded = selected && selected.is_negated;
          if (!excluded && selected) {
            new_values[label] = dvalues[key] || 0;
          } else if (selected) {
            new_values[key] = xfvalues[key] || 0;
          }
        });
        dvalues = new_values;
      }
      dvalues = new_values;
    // Get all values for insight widgets
    } else if (type==="Insight") {
      if (value_labels && Object.values(value_labels).length !== 0) {
        Object.entries(value_labels).forEach((entry) => { 
          const key = entry[0];
          const label = entry[1];
          new_values[label] = (dvalues[key] || 0);
        });
        dvalues = new_values;
      } else if (xfvalues && Object.values(xfvalues).length !== 0) {
        Object.entries(xfvalues).forEach((entry) => { 
          const key = entry[0];
          const label = entry[0];
          new_values[label] = (dvalues[key] || 0);
        });
        dvalues = new_values;
      } 
    }

    // Ad-hoc locations need counts so get the values (API call)
    if (chart === "locations") {
      await getAdHocLocationCounts(chart, dfilters).then((value) => {
        value.forEach((item) => {
          dvalues = {...dvalues, ...item}
        });
      });
    }

    // Date ranges have specific formatting of label and sums the entire range of values given.
    if (
      config.uiType === "birthdate-range" ||
      config.uiType === "date-range"
    ) {
      dvalues = counts_crossfiltered[value_series];
      if (Object.keys(dvalues).length) {
        dfilters = dfilters.map((filter) => [filter.friendly_name || formatDisplayDateRange(filter.value), getDateRangeValueSum(filter, dvalues) || 0]);
        dvalues = Object.fromEntries(dfilters);
      }
    }
    // Integer and Float ranges have specific formatting of label and sums the entire range of values given.
    if (
      config.uiType === "float-range" ||
      config.uiType.includes("int-range")
      ) {
      dvalues = counts_crossfiltered[value_series];
      if (Object.keys(dvalues).length) {
        dfilters = dfilters.map((filter) => [filter.friendly_name || formatDisplayRange(filter.value), getRangeValueSum(filter, dvalues) || 0]);
        dvalues = Object.fromEntries(dfilters);
      }
    }

    // if geo boundaries or LE geo boundaries: find the labels in filters
    if (
      value_series === `${geoPrefix}geo.${geoPrefix}county_fips` ||
      value_series === `${geoPrefix}geo.${geoPrefix}zip` ||
      value_series === `${geoPrefix}geo.${geoPrefix}congress` ||
      value_series === `${geoPrefix}geo.${geoPrefix}cbsa`
    ) {
      let new_values = {};

      let filter_src = value_series.split('.')[1];

      let geoFilters = filters[filter_src];

      if (geoFilters && Object.values(geoFilters).length !== 0) {
        Object.entries(geoFilters).forEach((entry) => { 
          let label = entry[1].friendly_name || entry[1].value;
          const value = entry[1].value;
          new_values[label] = dvalues[value] || xfvalues[value] || 0;
        });
        dvalues = new_values;
      } else if (dfilters && Object.values(dfilters).length !== 0) {
        Object.entries(dfilters).forEach((entry) => { 
          let label = entry[1].friendly_name || entry[1].value;
          const value = entry[1].value;
          new_values[label] = (dvalues[value] || xfvalues[value] || 0);
        });
        dvalues = new_values;
      } 
    }

    if ( config.ds === `${geoPrefix}geo` && (config.id === `${geoPrefix}state`) ) {
      let val_src = value_series;

      const stateVals = counts_filtered[val_src] || {};
      let new_values = {};
      if (dvalues && Object.values(dvalues).length !== 0) {
        if(type === "Filter"){
          Object.entries(dvalues).forEach((entry) => { 
            let label = entry[0];
            new_values[label] = (dvalues[label] || 0);
          });
        } else if(type === "Insight"){
          Object.entries(stateVals).forEach((entry) => { 
            let label = entry[0];
            new_values[label] = ( stateVals[label] || 0);
          });
        }
        dvalues = new_values;
      } 
    }

    // Build final data rows to return
    const values = Object.keys(dvalues).map((val, index) => {
      let label;
      label = formatDownloadData(val, units);
      const match_level = getMatchLevel(val, value_labels, dfilters, /* for download */ true);
      let retValues = [
        type, config.label,
        label || index + 1,
        dvalues[val] || 0,
        (config.excluded) ? "Y" : "N",
      ];
      match_level && retValues.push(match_level.charAt(0).toUpperCase() + match_level.substring(1).toLowerCase());
      retValues.push(getWidgetNotes(config));
      return retValues;
    });
    return values;
  }

  // Get selection match levels if present
  const getMatchLevel = (value, value_labels, dfilters, download = false) => {
    if (!dfilters || !value_labels) return;
    const getKeyByValue = (obj, value) => {
      return Object.keys(obj).find(key => obj[key] === value);
    }
    const filterKey = getKeyByValue(value_labels, value);
    if (filterKey) {
      const filter = dfilters?.find((item) => {
        return item.value === filterKey
      });
      if (filter?.match_value) {
        return filter.match_value;
      } else if (download) {
        return;
      } else {
        return "individual";
      }
    }
  }

  const getCSVHeaders = (hasMatchLevels = false) => {
    let headers;;
    if (!hasMatchLevels) {
      headers = ["Filter/Insight", "Widget", "Label", "Count", "Excluded", "Notes"];
    } else { 
      headers = ["Filter/Insight", "Widget", "Label", "Count", "Excluded", "Match Level", "Notes"];
    }
    return headers;
  }

  // Conditional notes per widget type.
  function getWidgetNotes(config) {
    let note = "";
    if (config.uiType.includes('location')) {
      note = "These are counts per location radius. If locations overlap the sum total of them will be incorrect.";
    } else if (config.uiType.includes('range')) {
      note = "These are counts per range. If ranges overlap the sum total of them will be incorrect."
    }
    return note;
  }

  // CSV download Ad-Hoc counts
  async function getAdHocLocationCounts(chart, _filters){
    let values = _filters.map((filter) => {
      return locationsCount(filter, chart)
        .then((response) => {
          const count = response.counts.__total__.all_filters;
          let data = {}
          const name = filter.location.name + " (lat: " + filter.location.lat + " lng: " + filter.location.lng + " radius: " + filter.value + ")";
          data[name] = count;
          return data;
        })
        .catch((error) => {
            console.log(JSON.stringify(error))
        })
    });
    values = values.flat();
    return Promise.all(values);
  }

  // Format function for date ranges
  function formatDisplayDateRange(date) {
    if (!date) return;
    const dates = date.split('-');
    const startDate = formatDisplayDate(dates[0]);
    let endDate = formatDisplayDate(dates[1]) || startDate;
    return startDate + ' - ' + endDate;
  }

  // Format strings with characters into quoted strings to preserve commas in CSV
  function formatDownloadData(val, units) {
    if (val) {
      if (units && val.match(/^[0-9-> ]+$/)) {
        val = val + units;
      }
      val = '"' + val + '"';
    }
    return val;
  }

  // Loop through and sum individual date counts for each filter date range
  function getDateRangeValueSum(filter, allValues) {
    const start = filter.value.split('-')[0];
    const end = filter.value.split('-')[1];
    let sum = 0;
    for (const [key, value] of Object.entries(allValues)) {
      if (key >= start && key <= end) {
        sum += value;
      }
    }
    return sum;
  }

  // Loop through and sum individual date counts for each filter date range
  function getRangeValueSum(filter, allValues) {
    const start = parseFloat(filter.value.split('|')[0]);
    const end = parseFloat(filter.value.split('|')[1]);
    let sum = 0;
    for (const [key, value] of Object.entries(allValues)) {
      if (key >= start && key <= end) {
        sum += value;
      }
    }
    return sum;
  }

  // Format YYYYMMDD into MM/DD/YYYY for CSV download.
  function formatDisplayDate(date) {
    if (!date) return;
    const year = date.substring(0, 4);
    const month = date.substring(4, 6);
    const day = date.substring(6, date.length);

    return month + '/' + day + '/' + year;
  }

   // Format ranges for CSV download.
  function formatDisplayRange(val) {
    if (!val) return;
    const range = val.split('|');
    const start = parseFloat(range[0]);
    let end = parseFloat(range[1]) || start;
    return start + ' - ' + end;
  }

  function restoreFromAudience(_id) {
    setAudienceLoaded(false);
    const collectAudienceData = (api_data) => {
      if(!api_data) {
        return;
      }

      setAudience(api_data);

      let _audience = api_data;

      if(Object.keys(_audience).length === 0) {
        localStorage.removeItem("am:audience:audience");
        setLocationTypePoints([]);
      };

      if (!('id' in _audience)) {
        _audience.location_type_points = location_type_points;
        _audience.location_type_ids = location_type_ids;
      }

      // restore ad_hoc locations
      if(_audience && _audience.filters && Object.keys(_audience.filters).includes("locations") ) {
        let _collect_adHoc = [];
        _audience.filters.locations.forEach((item) => {
          _collect_adHoc.push(item.location);
        })
        _audience.adhoc_points = _collect_adHoc;
      }

      setFilters(_audience.filters || {});
      if (_audience.is_complex) {
        setComplexFilters(_audience.complex_filters || {});
      } else {
        localStorage.removeItem("am:audience:is_complex");
        localStorage.removeItem("am:audience:complexFilters");
        localStorage.removeItem("am:audience:complexFields");
        setComplexFilters({});
        setIsComplex(false);
      }
      setWidgets(_audience.insights || []);
      setAdhocPoints(_audience.adhoc_points || []);
      setLocationTypeIds(_audience.location_type_ids || []);
      if (_audience.location_type_ids && _audience.location_type_ids.length && mapListData) {
        const pointsToRestore = [];
        _audience.location_type_ids.forEach((type_id) => {
            const matchingLocations = mapListData.filter(
                (l) => l.location_type_id === type_id
            );
            matchingLocations.forEach((location) => {
                const existingPoint = pointsToRestore.find(p => p.id === location.id);
                if (existingPoint) return;

                const series = `DIST_FROM_${location.lng}_${location.lat}`;
                pointsToRestore.push(
                    Object.assign({}, location, {
                        id: location.id,
                        type: "location_type_counts",
                        name: location.name,
                        series: series,
                        value: location.default_radius || null,
                    })
                );
            });
        });
        setLocationTypePoints(pointsToRestore);
      }
      if (typeof _audience.dedupe_level === 'undefined') {
        setDedupe(dedupeLevels[0]);
      } else {
        setDedupe(_audience.dedupe_level);
      }

      setLastChartParams("");
      setLastMapParams("");
      setLastLocationParams("");
      // Use debounced refresh to batch with other state changes from this function
      runCountRefresh();

      setPoints({});
      setH3Indexes({});

      if (_audience.map_state) {
        setMapCenter(_audience.map_state.map_center);
        setMapZoom(_audience.map_state.map_zoom);
      }
    }

    if(_id === "clear_all") {
      setAudience({});
      setFilters({});
      setWidgets([]);
      setLocationTypePoints([])
      setLocationTypeIds([]);
      setActiveCharts({});
      setComplexFilters({});
      setIsComplex(false);
      setDedupe(dedupeLevels[0]);
      localStorage.removeItem("explorer:layers:boundaries");
      localStorage.removeItem("am:audience:lastid");
      localStorage.removeItem("am:audience:audience");
      localStorage.removeItem("am:audience:audience_state");
      localStorage.removeItem("am:audience:filters");
      localStorage.removeItem("am:audience:location_type_ids");
      localStorage.removeItem("am:audience:adhoc_points");
      localStorage.removeItem("am:audience:complexFilters");
      localStorage.removeItem("am:audience:is_complex");
      localStorage.removeItem("am:audience:widgets");
      if (window.location.pathname === "/explorer") {
        window.location.hash = '';
        window.location.reload();
      }
    } else if(isNaN(_id)) {
      collectAudienceData(_id);
    } else {
      query("audience/get", { id: _id })
      .then((audience_data) => {
        setAudience(audience_data);
        collectAudienceData(audience_data);
      })
    }
  }

  async function add_adhoc_point(point) {
    const existingPoint = adhoc_points.filter(
      (p) => p.lng === point.lng && p.lat === point.lat
    );
    if (existingPoint.length > 0) {
      return;
    }

    const next_id = (adhoc_points[adhoc_points.length - 1] || { id: 0 }).id + 1;
    const name = point.name || "Adhoc #" + next_id;
    const { series } = { series: `DIST_FROM_${point.lng}_${point.lat}` };

    const loc = Object.assign({}, point, {
      id: next_id,
      type: "adhoc",
      name: name,
      series: series,
    });
  
    setAdhocPoints((prevValue) => {
      if(!prevValue.some(item => item.id === loc.id)){
        prevValue.push(loc);
      }
      return [...prevValue];
    });
  }

  async function add_locations(locations) {
    if (Object.keys(locations).length === 0) {
    } else {
      locations.forEach((location) => {
        const existingPoint = location_type_points.filter(
          (p) => p.lng === location.lng && p.lat === location.lat
        );
        setLocationTypeIds((prevValue) => {
          if (!prevValue.includes(location.location_type_id)) {
            prevValue.push(location.location_type_id);
          }
          return [...prevValue];
        });

        if (existingPoint.length > 0) {
          return;
        }

        const { series } = {
          series: `DIST_FROM_${location.lng}_${location.lat}`,
        };

        const loc = Object.assign({}, location, {
          id: location.id,
          type: "adhoc",
          name: location.name,
          series: series,
          value: location.default_radius || null,
        });

        setLocationTypePoints((prevValue) => {
          prevValue.push(loc);
          return [...prevValue];
        });
      });
    }
  }

  function remove_locations(locations) {
    const location_ids = [];
    locations.forEach((location) => {
      location_ids.push(location.id);
      setLocationTypeIds((prevValue) => {
        prevValue = prevValue.filter((id) => id !== location.location_type_id);
        return [...prevValue];
      });
    });
    setLocationTypePoints((prevValue) => {
      return prevValue.filter((p) => !location_ids.includes(p.id));
    });
    const new_filters = (filters[known_chart_ids.locations] || []).filter(
      (f) => !(f.location && location_ids.includes(f.location.id))
    );
    set_filters(known_chart_ids.locations, new_filters);

    let _updated_ad_hoc = [];
    new_filters && new_filters.length && new_filters.forEach((item) => {
      _updated_ad_hoc.push(item.location);
    })
    setAdhocPoints(_updated_ad_hoc);
    localStorage.setItem( "am:audience:adhoc_points", JSON.stringify(_updated_ad_hoc) );
  }

  async function add_location_type_icon(locations) {
    if (Object.keys(locations).length === 0) {
    } else {
      const updateLocationsdata = () => {
        locations.forEach((location) => {
          const existingPoint = location_type_points.filter(
            (p) => p.id === location.id
          );
          setLocationTypeIds((prevValue) => {
            if (!prevValue.includes(location.location_type_id)) {
              prevValue.push(location.location_type_id);
            }
            return [...prevValue];
          });

          if (existingPoint.length > 0) {
            return;
          }

          const { series } = {
            series: `DIST_FROM_${location.lng}_${location.lat}`,
          };

          const loc = Object.assign({}, location, {
            id: location.id,
            type: "location_type_counts",
            name: location.name,
            series: series,
            value: location.default_radius || null,
          });

          setLocationTypePoints((prevValue) => {
            prevValue.push(loc);
            return [...prevValue];
          });
        });
      }

      updateLocationsdata();
    }
  }

  function remove_location_type_icon(locations) {
    const location_ids = [];
    locations.forEach((location) => {
      location_ids.push(location.id);
      setLocationTypeIds((prevValue) => {
        prevValue = prevValue.filter((id) => id !== location.location_type_id);
        return [...prevValue];
      });
    });
    setLocationTypePoints((prevValue) => {
      return prevValue.filter((p) => !location_ids.includes(p.id));
    });
    const new_filters = (filters[known_chart_ids.locations] || []).filter(
      (f) => !(f.location && location_ids.includes(f.location.id))
    );
    set_filters(known_chart_ids.locations, new_filters);
  }

  function milesToMeters(val) {
    return (val * 5280 * 12 * 2.54) / 100;
  }

  const build_location_selects = useCallback((location_filters, force_build = false) => {
    const current_params = JSON.stringify(location_filters);
    const params_changed = current_params !== last_location_params;
    if ((params_changed || force_build) && location_filters.length > 0) {
      const xhrConfig = {
        base_url: `${window.crossfilter_url}`,
      };
      let location_groups = ["Adhoc Locations"];
      location_filters.forEach((filter) => {
        if (
          filter.location.location_type__name &&
          !location_groups.includes(filter.location.location_type__name)
        ) {
          //location_groups.push(filter.location.location_type__name);
        }
      });

      location_groups.forEach((group) => {
        let id = "#" + group.replace(/ /g, "_") + `_${audience_uuid}`;
        const params = {
          id: id.toLowerCase(),
          label: group,
          plist: location_filters.map((f) => {
            return {
              file_id: "none",
              value: `adhoc_${f.location.id}`,
              label: f.location.name,
              cond: {
                compare_string: {
                  radius: {
                    lng: f.location.lng,
                    lat: f.location.lat,
                    radius_meters: parseInt(milesToMeters(f.value)),
                  },
                },
              },
            };
          }),
        };
        const locationChart = chartsStore.charts[known_chart_ids.locations];
        if (locationChart) {
          params.id = `#adhoc_${explorerModule}_locations_${audience_uuid}`;

          command(
            `create/feature/${appState.activeCompany.code}/${locationChart.ds}`,
            params,
            xhrConfig
          ).then((response) => {
            if (response && location_filters.length) {
              setLastLocationParams(JSON.stringify(location_filters));
              setLocationsFeatureBuilt(Date.now());
            }
          });
        }
      });
    } else {
      setLastLocationParams(JSON.stringify([]));
      // Only set locationsFeatureBuilt if not already set (avoid triggering refresh on every call)
      if (!locationsFeatureBuilt) {
        setLocationsFeatureBuilt(Date.now());
      }
    }
    // eslint-disable-next-line
  }, [[loaded, filters, complexFilters, adhoc_points, known_chart_ids]]);
  // eslint-disable-next-line
  const debouncedBuildLocationSelects = useMemo(() => debounce(build_location_selects, 1000), []);

  function setMapUpdateFunction(onMapUpdate) {
    setOnMapUpdateHandler(() => onMapUpdate);
  }

  function remove_adhoc_point(loc, locType) {
    setAdhocPoints((prevValue) => {
      return prevValue.filter((p) => p.id !== loc.id);
    });
    const new_filters = (filters[known_chart_ids.locations] || []).filter(
      (f) => !(f.location && f.location.id === loc.id)
    );
    set_filters(known_chart_ids.locations, new_filters);
  }

  function remove_all_adhoc_points() {
    const adHocLocs = adhoc_points;
    const customLocs = filters[known_chart_ids.locations] || [];
    adHocLocs.forEach((loc) => {
      remove_adhoc_point(loc);
    });
    customLocs.forEach((loc) => {
      remove_adhoc_point(loc);
    });
  }

  function filtersContainLifeEvents() {
    if( explorerModule === "life_events" || explorerModule === "movers" ) {
      return true;
    } else {
      return false;
    }
  }

  function updateFiltersForLEGeo(filters) {

    // Update known_chart_ids using the prefix
    chartsStore.known_chart_ids.zipcodes               = `${geoPrefix}zip`;
    chartsStore.known_chart_ids.counties               = `${geoPrefix}county_fips`;
    chartsStore.known_chart_ids.congressional_districts = `${geoPrefix}congress`;
    chartsStore.known_chart_ids.cbsa                   = `${geoPrefix}cbsa`;
    chartsStore.known_chart_ids.states                 = `${geoPrefix}state`;

    // All possible prefixes to normalize FROM, so we always start from a clean base
    const allPrefixes = [
      window.LIFE_EVENT_GEO_PREFIX,
      window.MOVER_GEO_PREFIX,
      window.CONSUMER_GEO_PREFIX,
    ].filter(p => p !== ""); // only non-empty ones need stripping

    const geoFields = ['geo', 'state', 'zip', 'county_fips', 'congress', 'cbsa'];

    // Step 1: Normalize filters back to unprefixed baseline
    let normalized = JSON.stringify(filters);
    for (const p of allPrefixes) {
      const pGeo = `${p}geo`;
      // Strip prefixed geo.field compound keys first (order matters)
      for (const field of geoFields) {
        normalized = normalized.replaceAll(`"${pGeo}.${p}${field}"`, `"geo.${field}"`);
      }
      // Then strip standalone prefixed keys
      for (const field of geoFields) {
        normalized = normalized.replaceAll(`"${p}${field}"`, `"${field}"`);
      }
    }

    // Step 2: Apply the target prefix (no-op if prefix is "")
    let updated = normalized;
    if (geoPrefix) {
      for (const field of geoFields) {
        // Compound geo.field keys
        updated = updated.replaceAll(`"geo.${field}"`, `"${geoPrefix}.${geoPrefix}${field}"`);
        // Standalone field keys
        updated = updated.replaceAll(`"${field}"`, `"${geoPrefix}${field}"`);
      }
    }

    const updatedFilters = JSON.parse(updated);
    return JSON.stringify(filters) !== updated ? updatedFilters : filters;
  }


  const prevFiltersContainLE = useRef(null);

  function filters2json(is_model=false, copy_filters=null) {
    //Split geo and location filters out so the simple filters logic looks like
    // ( (geo boundaries OR'd) AND (location types OR'd with any adhoc locations)) AND (the rest of the filters)

    let _filters = filters;
    if(copy_filters) {
      // when copying an audience use the filters from that audience
      _filters = copy_filters;
    }

    let localFilters = updateFiltersForLEGeo(_filters);
    const currentContainsLE = filtersContainLifeEvents();
    const leStateChanged = prevFiltersContainLE.current !== currentContainsLE;
    
    // Only update filters if LE state changed
    if (leStateChanged) {
      let localFilters = updateFiltersForLEGeo(_filters);
      if (!isEqual(localFilters, _filters)) {
        setFilters(localFilters);
      }
      prevFiltersContainLE.current = currentContainsLE;
    }

    let geo_boundary_filters = [];
    let negated_geo_boundary_filters = [];
    let location_filters = [];
    let negated_location_filters = [];
    const root_node_filters = Object.entries(localFilters)
      .map(([chart_id, criteria]) => {
        let is_negated = false;
        // Check if the criteria contains a customer model, they are all named "model_###"
        const re = /^model_[0-9]+$/
        if (chart_id.match(re)) {
          is_model = true;
        }

        let chart_filters = criteria
          .map((f) => {
            if (f.is_negated) is_negated = true;
            let chart = chartsStore.chartsRef.current?.[chart_id];
            if (!chart) {
              // Only remove filters if charts have finished loading — if not loaded yet,
              // chartsRef.current is stale/empty and the chart_id may still be valid.
              if (chartsStore.loaded) {
                remove_all_filters(chart_id);
              }
              return null;
            } else if (chart_id === known_chart_ids.locations) {
              return {
                eq: {
                  feature: {
                    ds: chart.ds,
                    id: `#adhoc_${localStorage.getItem("am:explorer_module")}_locations_${audience_uuid}`,
                  },
                  value: `adhoc_${f.location.id}`,
                },
              };
            } else if (chart.uiType.includes("int-range") || chart.uiType === "float-range") {
              const parsed = f.value.split("|");
              const startVal = parsed[0];
              const endVal = parsed[1];
              return {
                between: {
                  feature: {
                    ds: chart.ds,
                    id: chart.id,
                  },
                  value: startVal,
                  value2: endVal,
                },
              };
            } else {
              //Check to see if value is a date range in the form of YYYYMMDD-YYYYMMDD
              const date_regex = /[12][0-9][0-9][0-9][0-9][0-9][0-9][0-9]-[12][0-9][0-9][0-9][0-9][0-9][0-9][0-9]/;
              if (f.value.match(date_regex)) {
                const start_date = f.value.substring(0,8);
                const end_date = f.value.substring(9,17);
                return {
                  between: {
                    feature: {
                      ds: chart.ds,
                      id: chart.id,
                    },
                    value: start_date,
                    value2: end_date,
                  },
                };
              } else {
                return {
                  eq: {
                    feature: {
                      ds: chart.ds,
                      id: chart.id,
                    },
                    // Adds a specific match level for 'selection matching' if .
                    ...(f.match_value && f.match_value !== "individual" && { match_level: f.match_value }),
                    value: f.value,
                  },
                };
              }
            }
          })
          .filter((node) => node != null);

        if (chart_filters.length > 0) {
          if ([known_chart_ids.states,
            known_chart_ids.zipcodes,
            known_chart_ids.counties,
            known_chart_ids.congressional_districts,
            known_chart_ids.cbsa].includes(chart_id)) {
            if (is_negated) {
              negated_geo_boundary_filters.push({
                  or: chart_filters,
              });
            } else {
              geo_boundary_filters.push({
                or: chart_filters,
              });
            }
            return null;
          } else if (known_chart_ids.locations === chart_id || chart_id.startsWith("location_type_")) {
            if (is_negated) {
              negated_location_filters.push({
                  or: chart_filters,
              });
            } else {
              location_filters.push({
                or: chart_filters,
              });
            }
            return null;
          } else {
            if (is_negated) {
              return {
                not: {
                  or: chart_filters,
                },
              };
            }
            return {
              or: chart_filters,
            };
          }
        }
        return null;
      })
      .filter((node) => node != null);

    let combined_geo_filters = [];
    if (geo_boundary_filters.length > 0) {
      combined_geo_filters.push({
        or: geo_boundary_filters,
      })
    }
    if (location_filters.length > 0) {
      combined_geo_filters.push({
        or: location_filters,
      })
    }

    let negated_combined_geo_filters = [];
    if (negated_geo_boundary_filters.length > 0) {
      negated_combined_geo_filters.push({
        or: negated_geo_boundary_filters,
      })
    }
    if (negated_location_filters.length > 0) {
      negated_combined_geo_filters.push({
        or: negated_location_filters,
      })
    }

    let result_filters = []
    if (combined_geo_filters.length > 0) {
      result_filters.push({
        and: combined_geo_filters,
      })
    }

    if (negated_combined_geo_filters.length > 0) {
      result_filters.push({
        and: [{
          not: {
            or: negated_combined_geo_filters,
          }
        }],
      })
    }

    if (root_node_filters.length > 0) {
      result_filters.push({
        and: root_node_filters,
      })
    }

    if ( is_model ) {
      // model only order so restrict to Epsilon
      let censor_node = {
        eq: {
          feature: {
            ds: window.epsilon_ds,
            id: window.epsilon_id,
          },
          value: window.epsilon_value,
        }
      }
      result_filters.push({
        and: [censor_node],
      })
    }

    let dsAllValue = appState.activeCompany.data_permissions.find(entry => entry.name === "ds_all")?.value;
    if (dsAllValue !== undefined && dsAllValue.length > 0) {
      const dsAll = dsAllValue.split(",");

      let _suppressed = getAllDatasets();

      let _data_sets = getAllowedDatasets(result_filters, dsAll, _suppressed);

      setAllowedDatasets(_data_sets);
    }

    return {
      and: result_filters,
    };
  }

  function getAllDatasets() {
    let _key = "am:explorer:datasets";
    let _explorer_module = localStorage.getItem("am:explorer_module");
    let cached_datasets = JSON.parse(sessionStorage.getItem(_key));
    let final_sets = {};

    const matchPopulationToDataset = (_data) => {
      let exclusives = [];
      for (let key in _data) {
        if(_data[key]?.population_domain === _explorer_module) {
          exclusives.push(_data[key]?.config)
        }
      }

      let suppressedExclusivesDatasets = {exclusives: exclusives};
      suppressedExclusivesDatasets.exclusives = suppressedExclusivesDatasets.exclusives.flat();

      final_sets = suppressedExclusivesDatasets;
    }

    if(cached_datasets) {
      matchPopulationToDataset(cached_datasets);
    } else {
      query("reference/allowedDatasetsConfig", {})
      .then((response) => {
        matchPopulationToDataset(response);
        sessionStorage.setItem(_key, JSON.stringify(response));
      })
      .catch((error) => {
        console.log("Failed to get all datasets: ", error)
      })
    }

    return final_sets;
  }

  function parseComplexFilters(jsonObject) {
    if (typeof jsonObject === "undefined" || jsonObject === null) {
      return { and: [] };
    } else if (
      Object.prototype.toString.call(jsonObject) === "[object Array]"
    ) {
      let result = [];
      jsonObject.forEach(function (element, index) {
        if (!element) return;
        if (typeof element.all === "object") {
          result.push(parseComplexFilters({ or: element }));
        } else {
          result.push(parseComplexFilters(element));
        }
      });
      return result;
    } else if (typeof jsonObject === "object") {
      let result = {};
      for (let key in jsonObject) {
        if (key === "!") {
          if (typeof jsonObject[key].all === "object") {
            result = {
              not: parseComplexFilters({ or: jsonObject[key] }),
            };
          } else {
            result = {
              not: parseComplexFilters(jsonObject[key]),
            };
          }
        } else if (key === "and") {
          result = {
            and: parseComplexFilters(jsonObject[key]),
          };
        } else if (key === "or") {
          result = {
            or: parseComplexFilters(jsonObject[key]),
          };
        } else if (key === "all") {
          let parts;
          if (jsonObject[key][0].var) {
            parts = jsonObject[key][0].var.split("__");
          }
          let values = [];

          const bday_range = "ameps__v-epsln-demog-000033";
          const year_range = "ameps__v-epsln-demog-000031";
          const age_range = "ameps__v-epsln-demog-000030";

          let node_to_search;
          if (
            jsonObject[key].length &&
            jsonObject[key][1] &&
            Object.keys(jsonObject[key][1]).length &&
            jsonObject[key][1].in
          ) {
            node_to_search = (
              jsonObject[key][1].in.length === 1)
              ?
              jsonObject[key][1].in[0]
              :
              jsonObject[key][1].in[1];
          }
          
          node_to_search && Array.isArray(node_to_search) && node_to_search.forEach(function (element) {
            if (element == null) return; // skip null/undefined slots from incomplete filter state
            const field_name = jsonObject.all[0].var;
            if( (field_name === year_range) || (field_name === bday_range) || (field_name === age_range) ) {
              let range = [];
              if( (field_name === year_range) || (field_name === age_range) ) {
                range = element.split('|');
              } else if(field_name === bday_range) {
                range = element.split('-');
              }
              if( (field_name === age_range) ) {
                let value = {
                  between: {
                    feature: {
                      ds: parts[0],
                      id: parts[1],
                    },
                    // age_range values need a leading zero '0' to work
                    value: range[0].startsWith('0') ? range[0] : '0' + range[0],
                    value2: range[1].startsWith('0') ? range[1] : '0' + range[1],
                  },
                };
                values.push(value);
              } else {
                let value = {
                  between: {
                    feature: {
                      ds: parts[0],
                      id: parts[1],
                    },
                    value: range[0],
                    value2: range[1]
                  },
                };
                values.push(value);
              }
            } else {
              let value = {
                eq: {
                  feature: {
                    ds: parts[0],
                    id: parts[1],
                  },
                  value: (getComplexValue(element))
                },
              };
              values.push(value);
            }
          });
          result = values;
        } else {
          result = parseComplexFilters(jsonObject[key]);
        }
      }
      return result;
    } else {
      return {
        t: jsonObject,
      };
    }
  }

  function getComplexValue(element) {
    if (element.title) {
      return element.value;
    } else {
      return element;
    }
  }

  function complexFilters2json() {
    return parseComplexFilters(complexFilters.logic);
  }

  function getComplexFields() {
    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
    };
    let _population = localStorage.getItem("am:explorer_module");
    let complexFields = query(
      `fetch/gui_group_advanced/${appState.activeCompany.code}/root_${_population}`,
      {population_domain: explorerModule},
      xhrConfig
    )
      .then((response) => {
        let filtered_response = {};
        for (let filter in response) {
          if (response[filter].hasOwnProperty('tags') &&
              response[filter].tags.hasOwnProperty('filterable') &&
              response[filter].tags.filterable === "true") {
            if (response[filter].ds_id === "le_geo") {
              response[filter].label = "Life Events: " + response[filter].label;
            }
            filtered_response[filter] = response[filter];
          }
        }

        return filtered_response;
      })
      .catch((error) => {
        console.log(`ERROR: gui_group_advanced: error: ${error}`);
        return null;
      });
    return complexFields;
  }

  function clear_cache() {
    const xhrConfig = {
      base_url: `${window.crossfilter_url}`,
      method: 'GET',
    };
    let cache_call = query(
      `clear/cache`,
      null,
      xhrConfig
    )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.log(`ERROR: clear_cache: error: ${error}`);
        return null;
      });
    return cache_call;
  }

  function setLoadingData(value = false){
    setLoadingChartData(value);
    setLoadingMapData(value);
  }

  // Remove not just filters but location type layers
  function removeAllLocationTypePoints() {
    setLocationTypePoints([]);
  }

  const setRefreshTrue = (event) => {
    setRequestCountRefresh(true);
  }

  function getCompanyHq(company) {
    let hq_loc = {};
    query("companies/get", { id: company.id })
      .then((response) => {
        if (response.hasOwnProperty("headquarter_location")) {
          hq_loc = {
            lat: response.headquarter_location.lat,
            lng: response.headquarter_location.lng
          }
          localStorage.setItem("am:audience:map:zoom", "9");
        } else {
          // US
          hq_loc = {
            lat: 41.5686,
            lng: -96.2698,
          }
          localStorage.setItem("am:audience:map:zoom", "4");
        }
        setCompanyHq(hq_loc);
        localStorage.setItem("am:audience:map:center", JSON.stringify(hq_loc));
      })
  }

  // trailing: true waits for state to stabilize - refresh only fires after 500ms of no changes
  // This naturally handles slow initialization without fixed timeouts
  const runCountRefresh = useMemo(() => debounce(setRefreshTrue, 500, { leading: false, trailing: true }), []);

  /* check if the email mandatory field is present */
  function hasMandatoryEmail() {
    let has_email = false;
    let complex_filters_str = JSON.stringify(complexFilters);
    if("email_1email_1_1" in filters) {
      if( !filters.email_1email_1_1.length ) {
        has_email = true; // 'email mandatory' exists but is NOT selected
      } else {
        has_email = false; // 'email mandatory' selected - happy path
      }
    } else if (isComplex && complex_filters_str.includes("Email Mandatory")) {
      has_email = false;  // 'email mandatory' is in complex filters
    } else {
      has_email = true; // 'email mandatory' NOT included
    }
    return has_email;
  }

  useEffect(() => {
    if (!appState.loaded || !loaded || !appState.activeCompany) { return; }
    getCompanyHq(appState.activeCompany);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState.activeCompany])

  useEffect(() => {
    if (companyHq) {
      localStorage.setItem("am:audience:map:hq", JSON.stringify(companyHq));
      localStorage.setItem("am:audience:map:center", JSON.stringify(companyHq));
    }
  }, [companyHq]);

  useEffect(() => {
    if (loaded && audienceLoaded) {
        const chart_id = known_chart_ids.locations;
        build_location_selects(filters[chart_id] || {}, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    if (loaded && audienceLoaded && !isComplex) {
        const chart_id = known_chart_ids.locations;
        build_location_selects(filters[chart_id] || {}, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, chartsStore.charts]);

  useEffect(() => {
    if (loaded && audienceLoaded && isComplex) {
      const chart_id = known_chart_ids.locations;
      build_location_selects(filters[chart_id] || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complexFilters]);

  useEffect(() => {
    if (loaded && audienceLoaded && adhoc_points?.length) {
        const chart_id = known_chart_ids.locations;
        build_location_selects(filters[chart_id] || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adhoc_points]);

  useEffect(() => {
    if (chartsStore.loaded && appState.loaded) {
      getCompanyHq(appState.activeCompany);
      get_dedupe_levels();
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartsStore.loaded, appState.loaded]);

  // Fetch location data once per company - shared by all components
  useEffect(() => {
    if (appState.loaded && appState.activeCompany?.id) {
      query("location/location-types-with-locations", { 
        companyId: appState.activeCompany.id,
        population_domain: explorerModule
      })
        .then(setLocationTypesData)
        .catch((error) => console.error("Failed to load location types:", error));
      query("location/company-radius-defaults", { am_company_id: appState.activeCompany.id })
        .then(setRadiusDefaults)
        .catch((error) => console.error("Failed to load radius defaults:", error));
      query("location/mapList")
        .then((response) => setMapListData(response.rows))
        .catch((error) => console.error("Failed to load map list:", error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState.loaded, appState.activeCompany?.id]);

  useEffect(() => {
    if (loaded) {
      const newMapReady = !map_visible || (!!map && !!map_bounds);
      if (newMapReady !== mapReady) {
        setMapReady(newMapReady);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, map_visible, map, map_bounds]);

  /* main check to see if we need to refresh counts */
  useEffect(() => {
    // Base conditions that must always be met
    if (!loaded || !mapReady || !locationsFeatureBuilt || !audienceLoaded) {
      return;
    }

    // Check what actually changed by comparing to refs
    const activeChartsChanged = !isEqual(active_charts, prevActiveCharts.current);
    const mapBoundsChanged = !isEqual(map_bounds, prevMapBounds.current);
    const mapZoomChanged = map_zoom !== prevMapZoom.current;
    const pointsLayersChanged = !isEqual(points_layers, prevPointsLayers.current);
    const hexLayersChanged = !isEqual(hex_layers, prevHexLayers.current);
    const dedupeChanged = dedupe !== prevDedupe.current;
    const complexFiltersChanged = !isEqual(complexFilters, prevComplexFilters.current);
    const adhocPointsChanged = !isEqual(adhoc_points, prevAdhocPoints.current);
    const locationsFeatureBuiltChanged = locationsFeatureBuilt !== prevLocationsFeatureBuilt.current;

    // Track if layers have been initialized (hex or points layers set)
    if (!layersInitialized.current && (hex_layers.length > 0 || points_layers.length > 0)) {
      layersInitialized.current = true;
    }

    // Don't allow first refresh until layers are initialized
    // This prevents refreshing with incomplete data
    if (!firstRefreshTime.current && !layersInitialized.current) {
      // Update refs but don't refresh yet
      if (activeChartsChanged) prevActiveCharts.current = active_charts;
      if (mapBoundsChanged) prevMapBounds.current = map_bounds;
      if (mapZoomChanged) prevMapZoom.current = map_zoom;
      if (pointsLayersChanged) prevPointsLayers.current = points_layers;
      if (hexLayersChanged) prevHexLayers.current = hex_layers;
      if (dedupeChanged) prevDedupe.current = dedupe;
      if (complexFiltersChanged) prevComplexFilters.current = complexFilters;
      if (adhocPointsChanged) prevAdhocPoints.current = adhoc_points;
      if (locationsFeatureBuiltChanged) prevLocationsFeatureBuilt.current = locationsFeatureBuilt;
      return; // Wait for layers to be initialized
    }

    // After first refresh, ignore map/layer changes for 3 seconds (components settling)
    const now = Date.now();
    const settlingPeriodMs = 3000;
    const isSettling = firstRefreshTime.current &&
      (now - firstRefreshTime.current) < settlingPeriodMs;

    // Map, layer, and location changes are ignored during settling period
    const effectiveMapBoundsChanged = !isSettling && mapBoundsChanged;
    const effectiveMapZoomChanged = !isSettling && mapZoomChanged;
    const effectivePointsLayersChanged = !isSettling && pointsLayersChanged;
    const effectiveHexLayersChanged = !isSettling && hexLayersChanged;
    const effectiveLocationsFeatureBuiltChanged = !isSettling && locationsFeatureBuiltChanged;

    // Check if anything changed - map/layer/location changes only count after settling
    const somethingChanged = activeChartsChanged || effectiveMapBoundsChanged ||
      effectiveMapZoomChanged || effectivePointsLayersChanged || effectiveHexLayersChanged || dedupeChanged ||
      complexFiltersChanged || adhocPointsChanged || effectiveLocationsFeatureBuiltChanged;

    // Update all refs to current values
    if (activeChartsChanged) prevActiveCharts.current = active_charts;
    if (mapBoundsChanged) prevMapBounds.current = map_bounds;
    if (mapZoomChanged) prevMapZoom.current = map_zoom;
    if (pointsLayersChanged) prevPointsLayers.current = points_layers;
    if (hexLayersChanged) prevHexLayers.current = hex_layers;
    if (dedupeChanged) prevDedupe.current = dedupe;
    if (complexFiltersChanged) prevComplexFilters.current = complexFilters;
    if (adhocPointsChanged) prevAdhocPoints.current = adhoc_points;
    if (locationsFeatureBuiltChanged) prevLocationsFeatureBuilt.current = locationsFeatureBuilt;

    // Only refresh if something actually changed
    // The trailing debounce ensures we wait for state to stabilize before firing
    if (somethingChanged) {
      runCountRefresh();
      // Record first refresh time to start map settling period
      if (!firstRefreshTime.current) {
        firstRefreshTime.current = now;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loaded,
    mapReady,
    locationsFeatureBuilt,
    map_bounds,
    map_zoom,
    points_layers,
    hex_layers,
    isComplex,
    complexFilters,
    dedupe,
    active_charts,
    adhoc_points,
    audienceLoaded,
  ]);

  useEffect(() => {
    runCountRefresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, complexFilters])

  useEffect(() => {
    if(!allowedDatasets || !allowedDatasets.length) {
      return;
    }
    if (requestCountRefresh) {
      refresh_counts();
      setRequestCountRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestCountRefresh, allowedDatasets]);

  useEffect(() => {
    if(loaded && (!allowedDatasets || !allowedDatasets.length)) {
      filters2json();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedDatasets, loaded])

  useEffect(() => {
    if (!loaded || missingFilters || isComplex || !audience.id) return;

    localStorage.setItem("am:audience:filters", JSON.stringify(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if(!dedupe || dedupe === 'undefined') {
      setDedupe("Individual");
      return;
    }
    localStorage.setItem("am:audience:dedupe_level", dedupe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dedupe]);

  useEffect(() => {
    localStorage.setItem("am:audience:widgets", JSON.stringify(widgets));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgets]);

  useEffect(() => {
    localStorage.setItem(
      "am:audience:adhoc_points",
      JSON.stringify(adhoc_points)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adhoc_points]);

  useEffect(() => {
    localStorage.setItem("am:audience:map:zoom", map_zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map_zoom]);

  useEffect(() => {
    localStorage.setItem("am:audience:map:center", JSON.stringify(map_center));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map_center]);

  useEffect(() => {
    localStorage.setItem("am:audience:uuid", audience_uuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience_uuid]);

  useEffect(() => {
    setAudienceLoaded(true);
    localStorage.setItem("am:audience:audience", LZString.compress(JSON.stringify(audience)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  useEffect(() => {
    if (isComplex && Object.keys(complexFilters).length) {
      localStorage.setItem("am:audience:complexFilters", LZString.compress(JSON.stringify(complexFilters)));
    } 
    const noComplexFilters = complexFilters && complexFilters.tree && (Object.keys(complexFilters).length && !Object.keys(complexFilters.tree.children1).length) ? true : false;
    setNoFilters(noComplexFilters);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complexFilters, isComplex]);

  // watch for changes based upon missing filters and set complex filters
  useEffect(() => { 
    if (!Object.keys(updatedComplexFilters).length) return;
    setComplexFilters(updatedComplexFilters);
  }, [updatedComplexFilters]);

  function checkFiltersExist() {
    if (!audienceLoaded) return;
    if (isComplex && complexFilters) {
      checkComplexFiltersExist(complexFilters);
    } else {
      checkSimpleFiltersExist(filters);
    }
  }

  // Check that data filters that are used still exist.
  function checkSimpleFiltersExist(_filters) {
    if (!loaded || missingFilters || !_filters || (!audience.id && !audience.status)) return;
    let charts = chartsStore.charts;
    charts = Object.entries(charts);
    let foundFilters;
    const updatedFilters = existingSimpleFilters(_filters, charts, foundFilters);
    const exists = isEqual(updatedFilters, _filters);
    setMissingFilters(!exists);
    setNoFilters(updatedFilters && updatedFilters.length === 0);
    return updatedFilters;
  }

  function checkComplexFiltersExist(complexFiltersToCheck) {
    let charts = chartsStore.charts;
    if (!charts) return [];
    // Recursive check on complex filters vs. available filters. 

    let missing = false;
    let availableFilters;
    charts = Object.entries(charts);
    availableFilters = JSON.parse(JSON.stringify(complexFiltersToCheck));
    availableFilters = existingComplexFilters(complexFiltersToCheck, charts, availableFilters);
    missing = !isEqual(availableFilters.tree.children1, complexFiltersToCheck.tree.children1);
    if (!isEqual(availableFilters, updatedComplexFilters)) {
      setUpdatedComplexFilters({ ...availableFilters });
    }
    setMissingComplexFilters(missing);
    if (!noFilters) {
      setNoFilters(availableFilters && !Object.keys(availableFilters.tree.children1).length);
    }
    return availableFilters;
  }
  
  function set_missing_filters(value) {
    setMissingComplexFilters(value);
    setMissingFilters(value);
  }

  function set_no_filters(value) {
    setNoFilters(value);
  }

  useEffect(() => {
    localStorage.setItem("am:audience:is_complex", JSON.stringify(isComplex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplex]);

  useEffect(() => {
    localStorage.setItem(
      "am:audience:location_type_ids",
      JSON.stringify(location_type_ids)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_type_ids,]);

  useEffect(() => {
    if(Object.keys(filters).length) {
      setLoadingChartData(false);
    }
  }, [filters])

  useEffect(() => {
    const prefix = explorerModule === 'life_events'
      ? window.LIFE_EVENT_GEO_PREFIX   // "le_"
      : explorerModule === 'movers'
      ? window.MOVER_GEO_PREFIX       // "mv_"
      : window.CONSUMER_GEO_PREFIX;   // ""

    setGeoPrefix(prefix);
  }, [explorerModule])

  useEffect(() => {
    // When mapListData loads, rebuild location_type_points if we have ids but no points
    if (mapListData && location_type_ids.length && !location_type_points.length) {
        const pointsToRestore = [];
        location_type_ids.forEach((type_id) => {
            const matchingLocations = mapListData.filter(
                (l) => l.location_type_id === type_id
            );
            matchingLocations.forEach((location) => {
                const existingPoint = pointsToRestore.find(p => p.id === location.id);
                if (existingPoint) return;

                const series = `DIST_FROM_${location.lng}_${location.lat}`;
                pointsToRestore.push(
                    Object.assign({}, location, {
                        id: location.id,
                        type: "location_type_counts",
                        name: location.name,
                        series: series,
                        value: location.default_radius || null,
                    })
                );
            });
        });
        if (pointsToRestore.length) {
            setLocationTypePoints(pointsToRestore);
        }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapListData, location_type_ids]);

  // eslint-disable-next-line
  // Replace the crossfilter_output object creation with useMemo
const crossfilter_output = useMemo(() => ({
    loaded: loaded,
    audienceLoaded: audienceLoaded,
    filters: filters,
    widgets: widgets,
    allowedDatasets: allowedDatasets,
    dedupe: getDedupe(),
    setDedupe,
    dedupeLevels,
    active_charts: active_charts,
    adhoc_points: adhoc_points,
    location_type_points: location_type_points,
    location_type_ids: location_type_ids,
    locationTypesData: locationTypesData,
    radiusDefaults: radiusDefaults,
    mapListData: mapListData,
    count: count,
    counts_filtered: counts_filtered,
    counts_crossfiltered: counts_crossfiltered,
    map_visible: map_visible,
    map_covered: map_covered,
    map_bounds: map_bounds,
    map_center: map_center,
    map_zoom: map_zoom,
    companyHq: companyHq,
    points_layers: points_layers,
    hex_layers: hex_layers,
    loading_chart_data: loading_chart_data,
    loading_map_data: loading_map_data,
    setLoadingData: setLoadingData,
    points: points,
    h3_indexes: h3_indexes,
    runCountRefresh: runCountRefresh,
    setPointLayers: setPointLayers,
    setHexLayers: setHexLayers,
    setFilters: setFilters,
    setWidgets: setWidgets,
    setMapCovered: setMapCovered,
    chart_enter: chart_enter,
    chart_leave: chart_leave,
    map_move: map_move,
    map_enter: map_enter,
    map_leave: map_leave,
    setMapUpdateFunction: setMapUpdateFunction,
    add_filter: add_filter,
    remove_filter: remove_filter,
    remove_all_filters: remove_all_filters,
    add_empty_filter: add_empty_filter,
    set_filters: set_filters,
    set_complex_filters: set_complex_filters,
    remove_complex_filters: remove_complex_filters,
    checkSimpleFiltersExist: checkSimpleFiltersExist,
    checkComplexFiltersExist: checkComplexFiltersExist,
    checkFiltersExist: checkFiltersExist,
    setLocationTypePoints: setLocationTypePoints,
    add_widget: add_widget,
    remove_widget: remove_widget,
    add_adhoc_point: add_adhoc_point,
    remove_adhoc_point: remove_adhoc_point,
    remove_all_adhoc_points: remove_all_adhoc_points,
    update_location_filter: update_location_filter,
    update_location_filter_multiple: update_location_filter_multiple,
    locationsCount: locationsCount,
    removeAllLocationTypePoints: removeAllLocationTypePoints,
    filtersContainLifeEvents: filtersContainLifeEvents,
    add_locations: add_locations,
    remove_locations: remove_locations,
    add_location_type_filter: add_location_type_filter,
    remove_location_type_filter: remove_location_type_filter,
    add_location_type_icon: add_location_type_icon,
    remove_location_type_icon: remove_location_type_icon,
    restoreFromAudience: restoreFromAudience,
    save_audience: save_audience,
    delete_audience: delete_audience,
    quote_order: quote_order,
    save_order: save_order,
    submit_order: submit_order,
    build_model: build_model,
    score_model: score_model,
    getComplexFields: getComplexFields,
    missingFilters: missingFilters,
    missingComplexFilters: missingComplexFilters,
    noFilters: noFilters,
    set_no_filters: set_no_filters,
    set_missing_filters: set_missing_filters,
    isComplex: isComplex,
    complexFilters: complexFilters,
    audience,
    setAudience,
    clear_cache: clear_cache,
    validate_sftp_directory: validate_sftp_directory,
    getAudienceDownloadData: getAudienceDownloadData,
    hasMandatoryEmail,
    getDefaultMapZoom: getDefaultMapZoom,
    getFiltersReportData: getFiltersReportData,
    getFilterJson: getFilterJson,
    getInsightsReportData: getInsightsReportData,
    build_saved_selection_criteria: build_saved_selection_criteria,
    parseComplexFilters: parseComplexFilters,
    fetchGeoBoundaryNames: fetchGeoBoundaryNames,
    getMatchLevel: getMatchLevel,
    getCSVHeaders: getCSVHeaders,
    EXPLORER_MODULES: EXPLORER_MODULES,
    explorerModule: explorerModule,
    setExplorerModule: setExplorerModule,
    geoPrefix: geoPrefix,
    update_location_type_radius: update_location_type_radius
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    loaded,
    audienceLoaded,
    filters,
    widgets,
    allowedDatasets,
    dedupe,
    dedupeLevels,
    active_charts,
    adhoc_points,
    location_type_points,
    location_type_ids,
    count,
    counts_filtered,
    counts_crossfiltered,
    map_visible,
    map_covered,
    map_bounds,
    map_center,
    map_zoom,
    companyHq,
    points_layers,
    hex_layers,
    loading_chart_data,
    loading_map_data,
    points,
    h3_indexes,
    missingFilters,
    missingComplexFilters,
    noFilters,
    isComplex,
    complexFilters,
    audience,
    explorerModule,
  ]);

  return crossfilter_output;
}
